import { forwardRef, type HTMLAttributes, type KeyboardEvent } from "react";
import { cx } from "./cx";
import { getFocusable, rovingIndex } from "./focus";
import "./Toolbar.css";

export interface ToolbarProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name ("Mapping filters", "Bulk actions"). */
  label: string;
  /** Tighter gap and padding (table headers, card actions). */
  dense?: boolean;
}

const TEXT_ENTRY = "input:not([type='checkbox']):not([type='radio']), textarea, select";

/**
 * role="toolbar" row of controls. ←/→ (and Home/End) move focus between controls;
 * keys typed inside text inputs and selects are left alone. Every control stays in the
 * Tab order so forms inside toolbars remain easy to reach.
 */
export const Toolbar = forwardRef<HTMLDivElement, ToolbarProps>(function Toolbar(
  { label, dense = false, className, onKeyDown, children, ...rest },
  ref,
) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    if (target.closest(TEXT_ENTRY)) return;
    const items = getFocusable(e.currentTarget);
    const i = items.indexOf(target);
    if (i < 0) return;
    const next = rovingIndex(e.key, i, items.length, "horizontal");
    if (next === null) return;
    e.preventDefault();
    items[next].focus();
  };

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={label}
      aria-orientation="horizontal"
      className={cx("ui-toolbar", dense && "ui-toolbar--dense", className)}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </div>
  );
});

/** Flexible gap inside a Toolbar; everything after it aligns to the end. */
export function ToolbarSpacer() {
  return <span className="ui-toolbar__spacer" aria-hidden="true" />;
}

/** Thin vertical divider between toolbar groups. */
export function ToolbarDivider() {
  return <span className="ui-toolbar__divider" role="separator" aria-orientation="vertical" />;
}
